<template>
  <div class="fixed top-0 left-0 w-full min-w-full h-full min-h-full">
    <transition name="fade">
      <LoadingView
        v-if="showLoadingView"
        :loading="loading"
        @close="closeLoadingView"
      />
    </transition>

    <transition name="fade">
      <div
        v-if="!showLoadingView"
        class="flex justify-center items-center w-full min-w-full h-full min-h-full"
      >
        <div class="flex bg-white max-w-5xl w-full shadow-lg overflow-hidden items-stretch">
          <div
            class="w-7/12 bg-center bg-cover"
            style="background-image: url('https://tools.playmore.golf/Compare/img/bg4.jpg')"
          />

          <div class="w-5/12 p-8 pl-9 pr-9">
            <div class="flex items-center mb-4">
              <img
                src="https://www.ix.golf/hs-fs/hubfs/IX-Logo-3-3.png?width=500&height=500&name=IX-Logo-3-3.png"
                alt="IX Logo"
                class="h-10 w-10 mr-3 object-contain"
              />
              <div>
                <div class="text-2xl font-bold text-gray-800 heading leading-none">
                  IX Club Membership Simulator!
                </div>
              </div>
            </div>

            <div class="text-sm text-gray-800 font-medium mt-8 mb-1 leading-relaxed">
              Please select a simulator mode
            </div>
            <div class="flex items-center text-center select-none cursor-pointer text-gray-400">
              <div
                class="border w-1/2 mr-1 bg-gradient-to-b from-gray-50 to-gray-100 p-2 hover:text-gray-600 transition-all duration-500"
                :class="version === 'Partner' ? 'text-gray-800 border-gray-500' : ''"
                @click="version = 'Partner'"
              >
                <div class="text-2xl">
                  <i class="fas fa-handshake-alt" />
                </div>
                <div class="text-sm font-medium mt-1">
                  Partner
                </div>
              </div>
              <div
                class="border w-1/2 ml-1 bg-gradient-to-b from-gray-50 to-gray-100 p-2 hover:text-gray-600 transition-all duration-500"
                :class="version === 'Prospect' ? 'text-gray-800 border-gray-500' : ''"
                @click="version = 'Prospect'"
              >
                <div class="text-2xl">
                  <i class="fas fa-user-plus" />
                </div>
                <div class="text-sm font-medium">
                  Prospect
                </div>
              </div>
            </div>

            <div
              v-if="version === 'Prospect'"
              class="flex mt-6"
            >
              <div>
                <div
                  class="bg-white hover:bg-gray-100 transition-all duration-500 border w-4 h-4 leading-none cursor-pointer flex justify-center items-center"
                  @click="compareOwnFlexi = !compareOwnFlexi"
                >
                  <i
                    class="fas text-xs text-gray-600 fa-check"
                    :class="compareOwnFlexi ? '' : 'opacity-0'"
                    style="margin-top: 2px"
                  />
                </div>
              </div>
              <div class="ml-2 text-sm text-gray-500">
                <div class="text-gray-700 font-semibold leading-none">
                  Compare own flexi membership
                </div>
                <div class="mt-1">
                  By default, we don't compare to their own flexi membership if they don't currently
                  have one
                </div>
              </div>
            </div>

            <div
              v-if="version === 'Prospect'"
              class="flex mt-6"
            >
              <div>
                <div
                  class="bg-white hover:bg-gray-100 transition-all duration-500 border w-4 h-4 leading-none cursor-pointer flex justify-center items-center"
                  @click="contactDetailsRequired = !contactDetailsRequired"
                >
                  <i
                    class="fas text-xs text-gray-600 fa-check"
                    :class="contactDetailsRequired ? '' : 'opacity-0'"
                    style="margin-top: 2px"
                  />
                </div>
              </div>
              <div class="ml-2 text-sm text-gray-500">
                <div class="text-gray-700 font-semibold leading-none">
                  Require contact data
                </div>
                <div class="mt-1">
                  Select this option if you would like the version that requires contact data
                </div>
              </div>
            </div>

            <div class="mt-8">
              <div
                class="w-full min-w-full text-sm font-medium p-4 text-center transition-all duration-500 text-white select-none border leading-none mt-2"
                :class="
                  isValid
                    ? 'bg-gray-800 hover:bg-gray-900 border-gray-800 cursor-pointer'
                    : 'bg-gray-300 text-gray-700 cursor-default'
                "
                @click="submit()"
              >
                Continue
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
  import LoadingView from '@/app/shared/components/base/loading/loadingView.vue';
  import { MemberSummary } from '@/app/shared/services/memberService/memberService';

  export default {
    name: 'IXDashboard',

    components: {
      LoadingView,
    },

    data() {
      return {
        showLoadingView: true,
        loading: true,
        version: null,
        compareOwnFlexi: false,
        contactDetailsRequired: false,
      };
    },

    computed: {
      isValid() {
        if (this.version === null) {
          return false;
        }

        return true;
      },
    },

    mounted() {
      // Set a timeout fallback to close loading view after 10 seconds
      const loadingTimeout = setTimeout(() => {
        if (this.showLoadingView) {
          console.warn('Loading timeout reached, closing loading view');
          this.loading = false;
          this.closeLoadingView();
        }
      }, 10000);
      
      MemberSummary()
        .then((result) => {
          clearTimeout(loadingTimeout);
          this.loading = false;
          if (!result.data.IsAdmin) {
            window.location.replace('https://members.playmore.golf/Auth#!/login');
          }
        })
        .catch((error) => {
          clearTimeout(loadingTimeout);
          console.error('Error loading member summary:', error);
          this.loading = false;
          this.closeLoadingView();
        });
    },

    methods: {
      submit() {
        let params = '';

        if (!this.contactDetailsRequired && this.version === 'Prospect') {
          params += '?contactDetailsRequired=false';
        }

        if (this.compareOwnFlexi && this.version === 'Prospect') {
          params += params ? '&compareOwnFlexi=true' : '?compareOwnFlexi=true';
        }

        // Route to IX Partner or Prospect
        if (this.version === 'Partner') {
          this.$router.push(`/IX/Partner${params}`);
        } else {
          this.$router.push(`/IX/Prospect${params}`);
        }
      },

      closeLoadingView() {
        this.showLoadingView = false;
      },
    },
  };
</script>
<style src="../../assets/site.css"></style>

